import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Bell, Zap, Settings, FileText, Megaphone, Calendar, Lightbulb, GraduationCap, BarChart3, Sparkles, TrendingUp, ShieldAlert, RefreshCw, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUserProfile } from '@/contexts/user.context';
import {
  useNotificationQuery,
  useNotificationCountsQuery,
  useUpdateNotificationItemMutation,
} from '@/lib/query-options';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';
import type { NotificationItem } from '@/lib/types';

export function NotificationsMenu() {
  const [isOpen, setIsOpen] = useState(false);
  useUserProfile(); // Ensures user context is loaded
  const organizationData = useAtomValue(organizationDataAtom);

  const { data: notificationCounts } = useNotificationCountsQuery(
    organizationData?.id ?? 0,
    { is_archived: false }
  );
  const unreadCount = notificationCounts?.unread_count ?? 0;

  // Fetch unread, non-archived notifications (limit to 4)
  const { data: notificationData } = useNotificationQuery(
    {
      organization_id: organizationData?.id,
      is_archived: false,
      is_viewed: false,
    },
    1,
    4
  );

  const updateNotificationMutation = useUpdateNotificationItemMutation();

  const handleNotificationClick = async (notification: NotificationItem) => {
    if (!notification.is_viewed) {
      try {
        await updateNotificationMutation.mutateAsync({
          id: notification.id,
          updates: { is_viewed: true },
        });
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
  };

  const handleDismiss = async (e: React.MouseEvent, notification: NotificationItem) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await updateNotificationMutation.mutateAsync({
        id: notification.id,
        updates: { is_viewed: true },
      });
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };


  const getNotificationStyling = (notification: NotificationItem) => {
    switch (notification.warning_level) {
      case 'ERROR':
        return 'bg-red-50 hover:bg-red-100 border-l-4 border-red-500';
      case 'WARNING':
        return 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-yellow-500';
      case 'SUCCESS':
        return 'bg-green-50 hover:bg-green-100 border-l-4 border-green-500';
      case 'INFO':
      default:
        return 'bg-gray-50 hover:bg-gray-100 border-l-4 border-gray-200';
    }
  };

  // Source configuration for icons and colors
  const SOURCE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
    ACTIONS: {
      label: 'Actions',
      icon: Zap,
      color: 'text-blue-600',
    },
    CONFIGURATION: {
      label: 'Configuration',
      icon: Settings,
      color: 'text-purple-600',
    },
    WEEKLY_PERFORMANCE_REPORT: {
      label: 'Reports',
      icon: FileText,
      color: 'text-green-600',
    },
    CAMPAIGN_CREATION: {
      label: 'Campaign Creation',
      icon: Megaphone,
      color: 'text-amber-600',
    },
    daily_summary: {
      label: 'Daily Summary',
      icon: Calendar,
      color: 'text-indigo-600',
    },
    intelligence_report: {
      label: 'Intelligence Report',
      icon: Lightbulb,
      color: 'text-yellow-600',
    },
    onboarding: {
      label: 'Onboarding',
      icon: GraduationCap,
      color: 'text-teal-600',
    },
    strategic_report: {
      label: 'Google Ads Report',
      icon: BarChart3,
      color: 'text-rose-600',
    },
    v0_recommendations: {
      label: 'Recommendations (v0)',
      icon: Sparkles,
      color: 'text-violet-600',
    },
    v1_recommendations: {
      label: 'Recommendations (v1)',
      icon: TrendingUp,
      color: 'text-cyan-600',
    },
    keyword_sync: {
      label: 'Policy Violations',
      icon: ShieldAlert,
      color: 'text-red-600',
    },
    googleads_campaign_sync: {
      label: 'Google Ads Sync',
      icon: RefreshCw,
      color: 'text-orange-600',
    },
  };

  const getSourceConfig = (source: string) => {
    return SOURCE_CONFIG[source] || {
      label: source.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' '),
      icon: FileText,
      color: 'text-gray-700',
    };
  };

  const notifications = notificationData?.items || [];

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button className="relative h-9 w-9 rounded-full flex items-center justify-center text-gray-900 border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#290BD9] focus-visible:ring-offset-2">
          <Bell className="h-4 w-4" strokeWidth={2} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 flex items-center justify-center rounded-full bg-[#B91C1C] text-white text-xs font-medium">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-80 rounded-lg p-0"
        side="right"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b">
          <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
          {unreadCount > 0 && (
            <p className="text-xs text-gray-500">{unreadCount} unread</p>
          )}
        </div>

        {/* Notifications list */}
        <div className="max-h-[320px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm text-gray-500">No unread notifications</p>
            </div>
          ) : (
            <div className="py-1">
              {notifications.map((notification) => {
                const config = getSourceConfig(notification.source);
                const SourceIcon = config.icon;
                // Use link_url from metadata if available, otherwise fallback to notifications page
                const linkUrl = notification.item_metadata?.link_url || `/notifications/${notification.id}`;

                return (
                  <Link
                    key={notification.id}
                    to={linkUrl}
                    onClick={() => {
                      handleNotificationClick(notification);
                      setIsOpen(false);
                    }}
                    className={`block mx-1 my-1 rounded-md transition-colors cursor-pointer ${getNotificationStyling(notification)}`}
                  >
                    <div className="relative p-3">
                      <div className="pr-8">
                        {/* Source Badge with Icon */}
                        <div className="mb-1 flex items-center gap-1.5">
                          <SourceIcon className={`h-3.5 w-3.5 ${config.color}`} />
                          <span className={`text-xs font-medium ${config.color}`}>
                            {config.label}
                          </span>
                        </div>

                        <div className="text-sm font-normal text-gray-900 leading-tight line-clamp-2">
                          {notification.title}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDate(notification.created_at)}
                        </div>
                      </div>

                      <button
                        onClick={(e) => handleDismiss(e, notification)}
                        className="absolute top-1/2 right-3 transform -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* View All link */}
        <div className="border-t px-4 py-3">
          <Link
            to="/notifications"
            onClick={() => setIsOpen(false)}
            className="block text-center text-sm font-medium text-[#290BD9] hover:text-[#2309C5] transition-colors"
          >
            View All
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
